import type {
  FormValidationResults,
  HekateDisplayForm,
  HekateSubmitFormResult,
  JsonBlob,
  HekateDisplayField,
} from '@onshape/www-inertia';
import {
  L1Validations,
  FormIdUtil,
  FieldHtmlNameUtil,
  Trans,
  ErrorTagUtil,
  EloquaFieldValidationError,
} from '@onshape/www-inertia';
import WaUtil from '../../../util/WaUtil';
import ServiceEndpointsUtil from '../../../util/ServiceEndpointsUtil';
import LocalhostTestingUtil from '../../../util/LocalhostTestingUtil';
import hekateBus from '../../../../bus';
import ErrorUtil from '../../../util/ErrorUtil';
import AntiSpamUtil from '../../../util/AntiSpamUtil';
import TrackingUtil from '../../../util/TrackingUtil';
import ExternalUtil from '../../../ExternalUtil';
import ProgressiveProfiler from '../progressive-profiles/ProgressiveProfiler';
import FormSubmitResolutionProvider from './FormSubmitResolutionProvider';
import type { FormSubmitResolution } from '../../../../model/HekateFormSubmission';
import type { HekateSubmitFormLiteResult } from '../../../../model/HekateSubmitFormLiteResult';
import RedirectUtil from '../../../util/RedirectUtil';
import CatchUtil from '../../../../../utils/CatchUtil';
import { isE2eBot, isSignupTestEnabled } from '~/hekate-www/external/util/E2eTestingUtil';
import type { ButtonPortableText } from '~/sanity/types';
import UxProfileUtil from '~/hekate-www/external/util/UxProfileUtil';

/** The Main logic for submitting forms */
export default class FormSubmitter {
  private static maxNumberOfErrorsToShowPerField = 3;

  private formDefinition: Ref<HekateDisplayForm | null>;

  private formElement: Ref<HTMLFormElement | null>;

  private isSubmitDisabled!: Ref<boolean>;

  private isFormSubmitting: Ref<boolean>;

  private hasFailedRecaptchaV3: Ref<boolean>;

  private supportEmailText: Ref<string>;

  /** Redirect url on success (if not empty) */
  private redirectUrl: string;

  /** Google Analytics Conversion Goal Category */
  private gaCategory: string;

  /** Google Analytics Conversion Goal Action */
  private gaAction: string;

  private recaptchaSiteKey: Ref<string>;

  private isShowThankYou: Ref<boolean>;

  private isShowSuccessMessage: Ref<boolean>;

  private isShow409Error: Ref<boolean>;

  private appElement: Ref<HTMLFormElement | null>;

  /** Html to show on success (innerHTML) */
  private successMessagePortableText: Ref<ButtonPortableText | undefined>;

  private static readonly hekateFormSubmitCount = 'HekateFormSubmitCount';

  private static readonly hekateFormFailCount = 'HekateFormFailCount';

  private static readonly hekateFormFailCount409 = 'HekateFormFailCount409';

  private static readonly hekateFormFailCount422 = 'HekateFormFailCount422';

  private static readonly hekateFormFailCount500 = 'HekateFormFailCount500';

  private static readonly hekateFormFailCountTimeout = 'HekateFormFailCountTimeout';

  constructor({
    formDefinition,
    formElement,
    isSubmitDisabled,
    isFormSubmitting,
    supportEmailText,
    appElement,
    hasFailedRecaptchaV3,
    recaptchaSiteKey,
    isShowThankYou,
    isShow409Error,
    isShowSuccessMessage,
    redirectUrl,
    gaCategory,
    gaAction,
    successMessagePortableText,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>;
    formElement: Ref<HTMLFormElement | null>;
    isSubmitDisabled: Ref<boolean>;
    isFormSubmitting: Ref<boolean>;
    supportEmailText: Ref<string>;
    appElement: Ref<HTMLFormElement | null>;
    hasFailedRecaptchaV3: Ref<boolean>;
    recaptchaSiteKey: Ref<string>;
    isShowThankYou: Ref<boolean>;
    isShow409Error: Ref<boolean>;
    isShowSuccessMessage: Ref<boolean>;
    redirectUrl: string;
    gaCategory: string;
    gaAction: string;
    successMessagePortableText: Ref<ButtonPortableText | undefined>;
  }) {
    this.formDefinition = formDefinition;
    this.formElement = formElement;
    this.isSubmitDisabled = isSubmitDisabled;
    this.isFormSubmitting = isFormSubmitting;
    this.supportEmailText = supportEmailText;
    this.appElement = appElement;
    this.hasFailedRecaptchaV3 = hasFailedRecaptchaV3;
    this.recaptchaSiteKey = recaptchaSiteKey;

    this.isShowThankYou = isShowThankYou;
    this.isShow409Error = isShow409Error;
    this.isShowSuccessMessage = isShowSuccessMessage;

    this.redirectUrl = redirectUrl;
    this.gaCategory = gaCategory;
    this.gaAction = gaAction;

    this.successMessagePortableText = successMessagePortableText;
  }

  /** Return the result of the request or `null` on network error (e.g. blocked request, but NOT HTTP 401) */
  private async submit({
    evt,
    hekateSubmitId,
    hekateSubmitIdName,
  }: {
    evt: Event;
    hekateSubmitId: string;
    hekateSubmitIdName: string;
  }): Promise<HekateSubmitFormResult | null> {
    evt.preventDefault();

    if (this.formElement.value === null) {
      throw ErrorUtil.internalError({ msg: 'form element is null' });
    }
    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'form definition is null' });
    }

    setTimeout(() => {
      if (this.formElement.value === null) {
        return;
      }

      ExternalUtil.scrollToTopOfForm({ formId: this.formDefinition.value!.id });
    }, 250);

    const formDefinition = this.formDefinition.value;

    FormSubmitter.handleFormErrorMessages({ formDefinition });

    try {
      return await FormSubmitter.httpRequest(formDefinition, hekateSubmitId, hekateSubmitIdName);
    } catch (e) {
      console.error('Internal error: Could not submit form httpRequest', e);

      throw ErrorUtil.internalError({
        msg: 'Could not submit form httpRequest',
        error: CatchUtil.getMessage(e),
      });
    }
  }

  /** Set/Reset error messages. returns true if there are any errors. */
  public static handleFormErrorMessages({
    formDefinition,
  }: {
    formDefinition: HekateDisplayForm;
  }): boolean {
    const params = new URL(document.location.href).searchParams;
    const validationParam = params.get('L1Validations');
    if (validationParam === 'skip-frontend') {
      console.log('skipping validation on frontend for debug');
      return false;
    }
    FormSubmitter.clearErrors({ formDefinition });
    FormSubmitter.trimValues({ formDefinition });
    const results = L1Validations.checkValidation({ fields: formDefinition.displayFields });
    FormSubmitter.setErrors({ results, formDefinition });

    // Any field that doesn't have errors is said to be validated
    formDefinition.displayFields.forEach((field: HekateDisplayField) => {
      field.isValidated = field.errorMessages.length === 0;
    });
    if (results.hasErrors()) {
      console.warn(JSON.stringify(results));
    }

    return results.hasErrors();
  }

  /** Hand for the http part of the form submit */
  private static async httpRequest(
    form: HekateDisplayForm,
    hekateSubmitId: string,
    hekateSubmitIdName: string
  ): Promise<HekateSubmitFormResult | null> {
    try {
      return await this.innerHttpRequest(form, hekateSubmitId, hekateSubmitIdName);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e && typeof e.text === 'function') {
        try {
          return JSON.parse(await e.text());
        } catch (exc) {
          console.error('HekateSubmitError: Could parse json', exc);
        }
      }

      console.error('HekateSubmitError: Could not submit form httpRequest', e);

      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateSubmit,
        msg: 'Could not submit form httpRequest',
        error: CatchUtil.getMessage(e),
      });

      return null;
    }
  }

  /** Make the actual request and throw the response if it is an error response */
  private static async innerHttpRequest(
    form: HekateDisplayForm,
    hekateSubmitId: string,
    hekateSubmitIdName: string
  ): Promise<HekateSubmitFormResult> {
    const data: { [key: string]: string | boolean | number } = {};
    form.displayFields.forEach((field) => {
      if (field.dataType === 'none') {
        return;
      }
      data[field.htmlName] = field.val;
    });

    const token = await AntiSpamUtil.waitForNonExpiredCsrfToken();

    if (token === null) {
      const soft422 = {
        isSuccess: false,
        telemetryHekateId: '',
        id: '',
        statusCode: 422,
        statusText: 'refusing to send empty csrf',
        errBody: 'refusing to send empty csrf',
        err: null,
        recaptchaV2Key: '',
      };

      if (!AntiSpamUtil.getCsrfTokenOrNull()) {
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateCsrfToken,
          msg: 'SOFT 422: Could not get csrf token after waiting for it',
        });
      }

      return soft422;
    }

    const body = JSON.stringify({
      hekateSubmitId,
      hekateSubmitIdName,
      hekateFormId: form.id,
      csrfToken: AntiSpamUtil.getCsrfTokenOrNull(),
      signedScore: AntiSpamUtil.getSignedScore(),
      ...AntiSpamUtil.getTimings(),
      ...UxProfileUtil.getInteractions(),
      ...data,
    });

    const nounce = Math.round(Math.random() * (1 << 30)).toString(16);
    let url = `${ServiceEndpointsUtil.submitFormEndpoint}?nounce=${nounce}`;
    try {
      // Test if running as an end-to-end test
      if (!isSignupTestEnabled() && (LocalhostTestingUtil.getRecaptchaKeyOrNull() || isE2eBot())) {
        url = `/api/fb/devSubmitForm?nounce=${nounce}`;
      }
    } catch (e) {
      console.error('Could not find localStorage for e2eBot test', e);
    }

    const productGroup = ExternalUtil.getCookieStringByName('x-www-plan-group');
    const isInternalUser = ExternalUtil.getCookieStringByName('x-www-is-internal-developer');
    const isProductUser = ExternalUtil.getCookieStringByName('x-www-have-seen-on');

    const responsePromise = fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-www-plan-group': productGroup,
        'x-www-is-internal-developer': isInternalUser,
        'x-www-have-seen-on': isProductUser,
        ...(await AntiSpamUtil.commonFetchHeaders()),
      },
      body,
    });

    setTimeout(() => {
      hekateBus.dispatchEvent(
        'form-submit-http-request-started',
        new CustomEvent('form-submit-http-request-started', { detail: { formId: form.id } })
      );
    }, 1);

    const response = await responsePromise;
    if (response.status === 422) {
      AntiSpamUtil.csrfTokenTest({ hint: 'form-submitter' });
      await AntiSpamUtil.waitForCsrfToken({ isForce: true, timeoutMilliseconds: 1000 * 5 });
    }

    const expectedCodes = [201, 202, 403, 406, 409, 422];
    if (!expectedCodes.includes(response.status)) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateSubmit,
        msg: `Unexpected response.status: ${response.status}`,
      });

      throw response;
    }
    try {
      const json = await response.json();
      return json;
    } catch (e) {
      console.error(`Could not parse json: ${e}`);
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateSubmit,
        msg: 'Could not parse json',
      });
      throw e;
    }
  }

  /** Clear all errors */
  private static clearErrors({ formDefinition }: { formDefinition: HekateDisplayForm }) {
    formDefinition.displayFields.forEach((field) => {
      field.errorMessages.splice(0, field.errorMessages.length);
      field.isValidated = false;
    });
    formDefinition.errorMessages.splice(0, formDefinition.errorMessages.length);
  }

  /** Set the errors message within the form itself */
  private static setErrors({
    results,
    formDefinition,
  }: {
    results: FormValidationResults;
    formDefinition: HekateDisplayForm;
  }) {
    if (!results.hasErrors()) {
      return;
    }
    results.getFieldErrors().forEach((fieldError) => {
      const errosCount = formDefinition.displayFields[fieldError.index]?.errorMessages?.length || 0;
      if (errosCount >= FormSubmitter.maxNumberOfErrorsToShowPerField) {
        console.warn('Too many field errors errors', fieldError);
        return;
      }

      WaUtil.waPush({
        event: 'FormFieldErrorAdded',
        data: {
          formId: formDefinition.id,
          fieldName: formDefinition.displayFields[fieldError.index]?.htmlName || 'unknown-field', // TODO: Check the default value here
          message: fieldError.message,
        },
      });

      formDefinition.displayFields[fieldError.index]?.errorMessages.push({
        message: fieldError.message,
        hint: fieldError.hint,
      });
    });

    results.getErrors().forEach((error) => {
      formDefinition.errorMessages.push(error);

      WaUtil.waPush({
        event: 'FormErrorAdded',
        data: {
          formId: formDefinition.id,
          message: error.message,
        },
      });
    });
  }

  private static trimValues({ formDefinition }: { formDefinition: HekateDisplayForm }) {
    formDefinition.displayFields.forEach((field) => {
      if (typeof field.val !== 'string') {
        return;
      }

      field.val = field.val.trim();
    });
  }

  public async onSubmitForm({ evt }: { evt: Event }): Promise<HekateSubmitFormLiteResult> {
    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'form definition is null' });
    }

    FormSubmitter.conditionallyClearOldErrorCounts();

    // casting safe because null check above
    const recaptchaField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
      fieldName: FieldHtmlNameUtil.recaptcha,
    });
    const recaptchaIdField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
      fieldName: FieldHtmlNameUtil.recaptchaId,
    });
    const recaptchaVersionField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
      fieldName: FieldHtmlNameUtil.recaptchaVersion,
    });

    if (recaptchaVersionField && recaptchaVersionField.val === 'v3' && recaptchaIdField) {
      if (window.grecaptcha === null) {
        throw ErrorUtil.internalError({ msg: 'grecaptcha is null' });
      }

      if (recaptchaField) {
        await window.grecaptcha
          .execute(recaptchaIdField.val, {
            action: 'invite',
          })
          .then((v: string) => {
            let val: string;
            // change token to garbage for autofail
            if (new URL(document.location.href).searchParams.get('poison-recaptcha') === 'v3') {
              val = 'poison-v3';
            } else {
              val = v;
            }

            recaptchaField.val = val;
          });
      }
    }

    // check if recaptchaField is empty
    if (
      this.formDefinition.value.isRecaptcha &&
      recaptchaField &&
      recaptchaVersionField &&
      (!recaptchaField.val || !recaptchaVersionField.val)
    ) {
      // error out
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateRecaptcha,
        msg: 'Recaptcha fields are empty',
      });
      const failMessage = {
        message: Trans.by(
          'Recaptcha Expired, please solve recaptcha and submit again',
          'recaptchaExpiredError'
        ),
        hint: 'recaptcha-expired',
      };
      ErrorUtil.addSpecificFormError({
        formDefinition: this.formDefinition,
        errorMessages: [failMessage],
      });
      throw new Error('Recaptcha Fields are empty');
    }

    const submitField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>,
      fieldName: FieldHtmlNameUtil.submit,
    });

    if (!submitField) {
      console.error('no submit field');
      return {
        isSuccess: false,
        statusCode: 0,
      };
    }

    // cannot submit without proper email, show error.
    const formHasErrors = FormSubmitter.handleFormErrorMessages({
      formDefinition: (this.formDefinition as Ref<HekateDisplayForm>).value,
    });
    if (formHasErrors) {
      this.formDefinition.value.errorMessages.splice(
        0,
        this.formDefinition.value.errorMessages.length,
        {
          message: Trans.byKey('formGenericFieldErrorMessage'),
          hint: 'generic-error',
        }
      );

      ExternalUtil.scrollToTopOfForm({ formId: this.formDefinition.value!.id });
      evt.preventDefault();

      console.error('form has errors');
      return {
        isSuccess: false,
        statusCode: 0,
      };
    }

    // clear any old logs
    if (this.formDefinition.value.errorMessages.length !== 0) {
      this.formDefinition.value.errorMessages.splice(
        0,
        this.formDefinition.value.errorMessages.length
      );
    }

    if (this.formElement.value && !this.formElement.value.checkValidity()) {
      this.formElement.value.reportValidity();

      console.error('form did not validate');
      return {
        isSuccess: false,
        statusCode: 0,
      };
    }

    this.isFormSubmitting.value = true; // this should also make the form button disabled or un-clickable
    const tic = Date.now(); // used to see if there should be a timeout before showing the form again (a throttle on form submission)

    // This value is not passed to eloqua only used to compare the www -> formfort link
    const hekateSubmitId = [
      'ff', // prefix
      'aa',
      Date.now().toString(16), // ordered by date

      Math.round((1 << 30) * Math.random()).toString(16), // entropy
    ].join('-');

    const submitCount = FormSubmitter.incrementAndGetFormSubmitCount();
    const hekateSubmitIdName = `hekate-submit-id-${submitCount}`;
    setTimeout(() => {
      TrackingUtil.mouseFlowPush(['setVariable', 'hekate-submit-id-latest', hekateSubmitId]);
      if (submitCount <= 10) {
        TrackingUtil.mouseFlowPush(['setVariable', hekateSubmitIdName, hekateSubmitId]);
      }
    }, 10);

    let isFormSubmitSuccess: boolean;
    let statusCode: -1 | 9000 | 201 | 202 = -1;

    // create a promise for the submit and another one to query for the submitId
    // (delayed).  If the query wins the race, assume success and return else
    // look at the result from the form submit to consider errors.
    try {
      // query to see if the backend suggests any custom reaction to the submitted data
      const formSubmitResolution = FormSubmitResolutionProvider.resolve({
        formDefinition: this.formDefinition,
        successMessagePortableText: this.successMessagePortableText,
      });

      // race the full submit and a submit-id, return the result and tag to say which one won the race
      const submit = this.submit({
        evt,
        hekateSubmitIdName,
        hekateSubmitId,
      }).then((result) => ({ result, won: 'submit' }));

      // Send the enhanced GA data event
      this.enhancedGAConversionData();

      const submitResult = await submit;
      if (
        submitResult.result &&
        (submitResult.result.statusCode === 201 || submitResult.result.statusCode === 202)
      ) {
        this.handleFormSubmitSuccess({
          dataLayerPushData: submitResult.result.dataLayerPushData,
          formSubmitResolution,
        });
        isFormSubmitSuccess = true;
        statusCode = submitResult.result.statusCode;
      } else {
        this.handleFormSubmitError(submitResult.result);
        isFormSubmitSuccess = false;
      }
      // if failed v3, show v2, and don't scroll up until fully submitted
    } finally {
      const timeout = !this.supportEmailText.value && Date.now() - tic < 2500 ? 2500 : 0;
      setTimeout(() => {
        this.isFormSubmitting.value = false;
        ExternalUtil.safeResetRecaptcha();
        setTimeout(() => {
          if (!this.appElement.value) {
            console.warn('appElement is null');
            return;
          }

          ExternalUtil.scrollToTopOfForm({ formId: this.formDefinition.value!.id });
        }, 250);
      }, timeout);
    }

    return {
      isSuccess: isFormSubmitSuccess,
      statusCode,
    };
  }

  /** Try to see if we can do anything smarter with the general error. */
  private async handleFormSubmitError(result: HekateSubmitFormResult | null) {
    if (!this.formDefinition.value) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateForm,
        msg: 'form definition not set',
      });
      return;
    }

    // special handling when eloqua is down
    if (result !== null && result.statusCode >= 500 && FormSubmitter.isEloquaDown()) {
      ProgressiveProfiler.onEloquaDown({
        formDefinition: this.formDefinition,
        redirectUrl: this.redirectUrl,
      });
      this.buildSupportEmailErrorText('elq-down');
      return;
    }

    let message = Trans.byKey('formDefaultErrorMessage');
    // handle a "no response" from the server case
    if (result === null) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateForm,
        msg: 'no data from hekate',
      });

      if (FormSubmitter.isManyNullErrors()) {
        message = Trans.byKey('formSupportEmailErrorMessage');
        this.buildSupportEmailErrorText('many-nulls');
      } else {
        message = Trans.byKey('formServerNotReachableErrorMessage');
      }

      this.formDefinition.value.errorMessages.push({
        message,
      });
      return;
    }

    if (result.statusCode === 422) {
      if (AntiSpamUtil.getCsrfTokenOrNull() === null) {
        setTimeout(() => {
          const nonce = Math.round(Math.random() * 1e7).toString(16);

          console.warn(
            `\n----\nSecurity csrfToken is null. Please verify https://${document.location.hostname}/api/fb/crossSession?sso-data-query=heartbeat&nonce=${nonce} is not blocked by your firewall. Then refresh the page and try again.\n----\n`
          );
        }, 1000);
      }
      if (FormSubmitter.isMany422s()) {
        this.buildSupportEmailErrorText('many-422s');
      } else {
        message = Trans.byKey('formErrorSecurityTokenExpired');
        this.formDefinition.value.errorMessages.push({
          message,
        });
      }
      return;
    }

    // Handle cad `/api/invite` errors
    if (FormIdUtil.isCadInviteForm({ formId: this.formDefinition.value.id })) {
      if (result.statusCode === 409) {
        this.isShow409Error.value = true;
        if (FormSubmitter.isMany409s()) {
          RedirectUtil.redirectToPasswordReset();
        }

        return;
      }

      // handle recaptcha fail or expire
      if (result.statusCode === 401 || result.statusCode === 403) {
        if (result.recaptchaV2Key) {
          console.log('v3-recaptcha-fail');
          this.recaptchaSiteKey.value = result.recaptchaV2Key;
          this.hasFailedRecaptchaV3.value = true;
          TrackingUtil.mouseFlowPush(['tag', 'recaptcha-v3-fail']);
        } else {
          message = Trans.byKey('recaptchaHasExpired');
          this.formDefinition.value.errorMessages.push({
            message,
          });
          TrackingUtil.mouseFlowPush(['tag', 'recaptcha-expired']);
        }
        return;
      }
    }

    // Error with no body
    if (!result.errBody) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateForm,
        msg: 'no error body',
      });

      message = Trans.byKey('formUnknownEmptyEloquaBodyErrorMessage');
      this.formDefinition.value.errorMessages.push({
        message,
      });
      return;
    }

    // handle expected errors such as bad email address
    try {
      const errInfo = JSON.parse(result.errBody) as JsonBlob[];
      const errors = errInfo.map((val) => EloquaFieldValidationError.fromJsonBlob(val));

      // Attempt to put a generic error message around the field that likely has the error.
      if (errors.length > 0) {
        const err = errors[0];
        const itemId = err?.property;

        const errMessage = Trans.byKeyOrDefault({
          key: err?.transKey || '',
          defaultKey: 'formGenericFieldErrorMessage',
        });

        const foundIndex = this.formDefinition.value.displayFields.findIndex(
          (field) => field.id === `${itemId}`
        );
        if (foundIndex !== -1) {
          this.formDefinition.value.displayFields[foundIndex]?.errorMessages.push({
            message: errMessage,
            hint: 'generic-error',
          });
        }
      }
    } catch (e) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateForm,
        msg: 'error parsing errorInfo',
        error: CatchUtil.getMessage(e),
      });
      message = Trans.byKey('formErrorHandlingErrorMessage');
    }

    this.formDefinition.value.errorMessages.push({
      message,
    });
  }

  /** Handle success form submission */
  private handleFormSubmitSuccess({
    dataLayerPushData,
    formSubmitResolution,
  }: {
    dataLayerPushData?: JsonBlob;
    formSubmitResolution: Promise<FormSubmitResolution | null>;
  }) {
    this.pushDataLayer(dataLayerPushData);
    if (this.formDefinition.value !== null) {
      ProgressiveProfiler.persistAnswers({
        formDefinition: this.formDefinition.value,
      });
    } else {
      console.warn('formDefinition is null can not persist progressive profiles');
    }

    try {
      FormSubmitter.clearErrorCounts();
    } catch {
      // ignore localStorage is likely not enabled
    }

    const formId = this.formDefinition.value !== null ? this.formDefinition.value.id : '';
    if (!formId) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateInternalError,
        msg: 'no formId on success',
      });
    }
    hekateBus.dispatchEvent('form-successfully-submitted', {
      formDefinition: this.formDefinition.value,
      formSubmitResolution,
    });

    if (this.successMessagePortableText.value?.buttonPortableText) {
      this.isShowSuccessMessage.value = true;
    } else {
      this.isShowThankYou.value = true;
    }

    // Remove the title of the Form e.g. "Request a Demonstration"
    const headers = document.querySelectorAll('#form-header:not(.d-none #form-header)');
    headers.forEach((header) => {
      (header as HTMLElement).style.display = 'none';
    });

    ProgressiveProfiler.onSuccess({
      formDefinition: this.formDefinition,
      redirectUrl: this.redirectUrl,
      formSubmitResolution,
    });
  }

  // Pushes email and phoneNumber if present to Data Layer as enhanced conversion data
  private enhancedGAConversionData() {
    if (!this.formDefinition.value) return;

    const emailField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>, // safe because check above
      fieldName: FieldHtmlNameUtil.email,
    });

    const phoneNumberField = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>, // safe because check above
      fieldName: FieldHtmlNameUtil.phoneNumber,
    });

    const email = emailField && emailField.val ? emailField.val : '';
    const phoneNumber = phoneNumberField && phoneNumberField.val ? phoneNumberField.val : '';

    // remove spaces for GA
    const cleanedPhoneNumber = `${phoneNumber}`.replace(/\s+/g, '').replace(/-/g, '');

    const enhancedConversionData = {
      email,
      phoneNumber: cleanedPhoneNumber,
    };

    // don't send an entry with an empty value if not present
    const filteredEnhancedConversionData = Object.fromEntries(
      Object.entries(enhancedConversionData).filter((entry) => entry[1])
    );

    if (!filteredEnhancedConversionData) return;

    const conversionSubmitData: GoogleAnalyticsEventObject = {
      event: 'from_submit_enhance_in_network',
      enhanced_conversion_data: filteredEnhancedConversionData,
    };

    console.log('Pushing enhanced GA', conversionSubmitData);

    TrackingUtil.dataLayerPush(conversionSubmitData);
  }

  /** Push the Google Analytics event */
  private pushDataLayer(dataLayerPushData?: JsonBlob) {
    if (dataLayerPushData) {
      console.log('Pushing dataLayerPushData from Hekate', dataLayerPushData);
      TrackingUtil.dataLayerPush(dataLayerPushData as GoogleAnalyticsEventObject);
    }

    const errorCategory = 'Form Submitted with Unknown Name';
    if (!this.formDefinition.value) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateForm,
        msg: 'Unsupported GA no form definition',
      });

      TrackingUtil.dataLayerPush({
        event: 'EloquaFormSubmit',
        category: errorCategory,
        action: 'Success',
      });
      return;
    }

    const action = this.gaAction || 'Success';
    const category = this.gaCategory || this.formDefinition.value.name;
    const event = 'EloquaFormSubmit';
    const formSubmitData: GoogleAnalyticsEventObject = {
      event,
      category,
      action,
    };
    TrackingUtil.dataLayerPush(formSubmitData);

    console.log('Pushing Conversion', formSubmitData);

    const field = ExternalUtil.findDisplayFieldOrNull({
      formDefinition: this.formDefinition as Ref<HekateDisplayForm>, // safe because check above
      fieldName: FieldHtmlNameUtil.abVersion,
    });
    if (!field || !field.val) {
      return;
    }
    const abVariant = field.val;
    const abData: GoogleAnalyticsEventObject = {
      event: 'ABTest',
      category: 'A/B test',
      action: 'Form Submit',
      label: `${abVariant}`,
    };
    TrackingUtil.dataLayerPush(abData);

    console.log('Pushing A/B', abData);
  }

  /** Build the text the user can C&P into their email to contact support */
  public buildSupportEmailErrorText(
    errorCode: 'elq-down' | 'many-nulls' | 'signup-errors' | 'many-422s'
  ) {
    if (this.formDefinition.value === null) {
      throw ErrorUtil.internalError({ msg: 'form definition is null' });
    }

    const sentryMsg = `User shown support email error with error code - ${errorCode} formId - ${this.formDefinition.value.id}`;
    ErrorUtil.safePostMessageToSentry({ tag: ErrorTagUtil.hekateFailEmailSupport, msg: sentryMsg });

    const formName = this.formDefinition.value.name;
    const msgFields = this.formDefinition.value.displayFields.filter(
      (v) =>
        v.displayType === 'text' ||
        v.displayType === 'singleSelect' ||
        v.displayType === 'checkbox' ||
        v.displayType === 'textArea'
    );

    const msg = [];
    msg.push(Trans.byKey('fromSupportCopyAndPasteText'));
    msg.push('');
    msg.push('---');

    msg.push(`
Error Description:
User encountered error during signup on ${document.location.href}
User provided guidance to submit this information to support@onshape.com.`);
    if (FormIdUtil.isCadInviteForm({ formId: this.formDefinition.value.id })) {
      msg.push('User may not have been able to signup.');
    }

    msg.push('\n\n');
    msg.push('Form Details:');
    msg.push(`Form Name: ${formName}`);
    msg.push(`Browser: ${window.navigator.userAgent}`);
    msg.push(`Error Code: ${errorCode} (${Date.now()})`);
    msg.push(`WWW Form page: ${document.location.href}`);
    msg.push(`Csrf Token: ${AntiSpamUtil.getCsrfTokenOrNull() ? 'present' : 'missing'}`);
    msg.push(`WWW Form Submission Error Code: ${errorCode} (${Date.now()})`);
    msg.push(...msgFields.map((field) => `${field.name}: ${field.val}`));
    msg.push('\n\n\n');
    this.supportEmailText.value = msg.join('\n');
  }

  /** Clear error counts if they are too old to consider */
  private static conditionallyClearOldErrorCounts() {
    const ttl = 1000 * 60 * 10;
    try {
      const oldCount = window.sessionStorage.getItem(FormSubmitter.hekateFormFailCountTimeout);
      if (!oldCount) {
        const expiry = Date.now() + ttl;
        window.sessionStorage.setItem(FormSubmitter.hekateFormFailCountTimeout, `${expiry}`);
      }

      const count = window.sessionStorage.getItem(FormSubmitter.hekateFormFailCountTimeout);
      if (!count) {
        return;
      }

      const expiry = parseInt(count, 10);
      if (expiry < Date.now()) {
        FormSubmitter.clearErrorCounts();
        window.sessionStorage.removeItem(FormSubmitter.hekateFormFailCountTimeout);
      }
    } catch (e) {
      console.error('Could not parse expiry', e);
    }
  }

  /** Clear all error counts */
  private static clearErrorCounts() {
    window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount, '0');
    window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount409, '0');
    window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount500, '0');
  }

  /** Handle many no response from hekate errors */
  private static isManyNullErrors(): boolean {
    try {
      let count = parseInt(
        window.sessionStorage.getItem(FormSubmitter.hekateFormFailCount) || '0',
        10
      );
      if (count >= 1) {
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateSubmitFail,
          msg: 'many null errors',
        });
        TrackingUtil.mouseFlowPush(['setVariable', 'hekate-submit-fail', 'many-nulls']);
        window.sessionStorage.setItem('HekateFormFailCount', '0');
        return true;
      }
      count += 1;
      window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount, `${count}`);
    } catch {
      return false;
    }

    return false;
  }

  /** Handle many errors from cad */
  private static isMany409s(): boolean {
    try {
      let count = parseInt(
        window.sessionStorage.getItem(FormSubmitter.hekateFormFailCount409) || '0',
        10
      );
      if (count >= 1) {
        window.sessionStorage.setItem('HekateFormFailCount409', '0');
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateSubmitFail,
          msg: 'many 409s',
        });
        TrackingUtil.mouseFlowPush(['setVariable', 'hekate-submit-fail', 'many-409s']);
        return true;
      }
      count += 1;
      window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount409, `${count}`);
    } catch {
      return false;
    }

    return false;
  }

  /** Handle many csrf errors */
  private static isMany422s(): boolean {
    try {
      let count = parseInt(
        window.sessionStorage.getItem(FormSubmitter.hekateFormFailCount422) || '0',
        10
      );
      if (count >= 1) {
        window.sessionStorage.setItem('HekateFormFailCount422', '0');
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateSubmitFail,
          msg: 'many 422s',
        });
        TrackingUtil.mouseFlowPush(['setVariable', 'hekate-submit-fail', 'many-422s']);
        return true;
      }
      count += 1;
      window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount422, `${count}`);
    } catch {
      return false;
    }

    return false;
  }

  /** Handle 500 errors from Eloqua */
  private static isEloquaDown(): boolean {
    try {
      let count = parseInt(
        window.sessionStorage.getItem(FormSubmitter.hekateFormFailCount500) || '0',
        10
      );
      if (count >= 2) {
        window.sessionStorage.setItem('HekateFormFailCount500', '0');
        ErrorUtil.safePostMessageToSentry({
          tag: ErrorTagUtil.hekateSubmitFail,
          msg: 'many 500s',
        });
        TrackingUtil.mouseFlowPush(['setVariable', 'hekate-submit-fail', 'eloqua-down']);
        return true;
      }
      count += 1;
      window.sessionStorage.setItem(FormSubmitter.hekateFormFailCount500, `${count}`);
    } catch {
      return false;
    }

    return false;
  }

  /**
   * Increment a count in sessionStorage of how many times the form has been submitted mod 10
   * We limit to 10 so we don't have a huge number of variables
   */
  private static incrementAndGetFormSubmitCount(): number {
    try {
      let count = parseInt(
        window.sessionStorage.getItem(FormSubmitter.hekateFormSubmitCount) || '0',
        10
      );
      count += 1;
      if (count > 10) {
        count = 1;
      }
      window.sessionStorage.setItem(FormSubmitter.hekateFormSubmitCount, `${count}`);
      return count;
    } catch {
      return 1;
    }
  }
}
